import { useTranslation } from "react-i18next";
import TotalCard from "./index";
import { AnimatedNumber } from "../motion";
import { getBookingPriceBreakdown } from "../../utils/discount";

/**
 * Renders several bookings inside a single price card with one combined
 * grand total. Used on /cart and /checkout where the cart holds more than one
 * booking. Each booking's fare breakdown is rendered by reusing <TotalCard />
 * in "section" mode (no own wrapper, no own total footer); the combined grand
 * total is appended once at the bottom.
 *
 * For a single booking this is visually equivalent to a standalone TotalCard.
 */
const MultiTotalCard = ({
    carts = [],
    lang,
    className = "",
    title,
}) => {
    const { t } = useTranslation();

    const items = Array.isArray(carts) ? carts : [];

    // Same breakdown each TotalCard section uses, so the grand total is exactly the sum of
    // the per-booking totals. `totalPriceBeforeDiscount` is not read directly: the backend
    // leaves a stale value behind when a discount is removed, and quotation bookings never
    // get one.
    const breakdowns = items.map((cart) => getBookingPriceBreakdown(cart));

    const grandTotal = breakdowns.reduce((sum, b) => sum + b.totalPrice, 0);
    const grandTotalBeforeDiscount = breakdowns.reduce((sum, b) => sum + b.priceBeforeDiscount, 0);
    const hasDiscount = breakdowns.some((b) => b.hasDiscount);

    return (
        <div className={className}>
            <div className="card">
                {title && (
                    <div className="card-body tw:!border-b tw:!border-b-[#E8E8E8]">
                        <p className="tw:!mb-0 tw:font-libre-caslon-display tw:!font-[400] tw:!text-[22px] tw:!text-[#282828]">
                            {title}
                        </p>
                    </div>
                )}

                {items.map((cart, index) => (
                    <TotalCard
                        key={cart?.key ?? index}
                        cart={cart}
                        lang={lang}
                        isFoldable={false}
                        renderAsSection
                        showTotal={false}
                        useBorder={index < items.length - 1 ? true : false}
                    />
                ))}

                {/* Combined grand total across every booking */}
                <div className="card-body">
                    <div className="tw:!flex tw:!justify-start tw:!items-center">
                        <div className="tw:!flex tw:!justify-start tw:!items-center">
                            <p className="tw:!mb-0 tw:!font-[500] tw:!text-[16px] tw:!text-[#282828]">
                                {t("componentTotalCard.totalPrice")}
                            </p>&nbsp;
                            <p className="tw:!mb-0 tw:!font-[400] tw:!text-[16px] tw:!text-[#8D8D8D]">
                                {t("componentTotalCard.priceTax")}
                            </p>
                        </div>
                        <div className="col d-flex align-items-end flex-column pe-0 ">
                            {hasDiscount && grandTotalBeforeDiscount > grandTotal && (
                                <p className="text-end tw:!mb-0 tw:!text-[13px] tw:!font-[400] tw:!text-[#EA4335] tw:!line-through">
                                    <AnimatedNumber value={grandTotalBeforeDiscount} />
                                    {t("componentTotalCard.jpy")}
                                </p>
                            )}
                            <p className="tw:!mb-0 tw:!font-[700] tw:!text-[16px] tw:!text-[#282828]">
                                {/* Counts down when a booking is removed from the cart. */}
                                <AnimatedNumber value={grandTotal} />
                                {t("componentTotalCard.jpy")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiTotalCard;
